import { ReactElement, useMemo, useEffect } from 'react'
import { useParams } from 'react-router-dom'
import {
  StructuralPlanningElementFormHeader,
  TensileTransmitterList,
  VerticalTransmitterList,
} from '@structuralPlanningComponents'
import { AxiosError } from 'axios'
import { find } from 'lodash-es'
import { TabPanel, TabContext, TabList } from '@mui/lab'
import { Stack, styled, Tab } from '@mui/material'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { HotkeyDeleteButton } from '@ui/actions'
import { useSnackbar } from '@ui/snackbar'
import { Box } from '@ui/structure'
import { useModelStore, useTransmissionStore } from '@editorStores'
import {
  useBlockScene,
  useElementLabel,
  useElementType,
  useResultsInvalidation,
} from '@editorHooks'
import {
  CollapsibleAssemblyBoxSection,
  CollapsibleCopyBoxSection,
  CollapsibleDocumentationBoxSection,
  CollapsibleGeometryBoxSection,
  CollapsibleProblemsBoxSection,
  CollapsibleSettingsBoxSection,
  DocumentationItem,
} from '@editorComponents'
import {
  useStructuralPlanningFormEsc,
  useStructuralPlanningSelection,
} from '@structuralPlanningHooks'
import { getVerticalTransmissionGraph, getModel, getProjectFiles } from '@queries'
import {
  createColumn,
  deleteColumn,
  recalculateTensileTargets,
  updateColumn as updateColumnReq,
} from '@mutations'
import { buildErrorMessage, documentationRootGuid } from 'src/constants'
import { useElementHistory } from 'src/hooks'
import SingleElementCSForm from '../../../../../../components/SingleElementCSForm'
import { useSaveTensileTransmissionGraph } from '../../../../hooks/useSaveTensileTransmissionGraph'
import CopyToOtherStoreys from '../../../MultiEdit/components/CopyToOtherStoreys'
import PlanSettingsForm from '../../../PlanSettingsForm'
import GeometryForm from './GeometryForm'
import { columnTabs } from './tabs'

const StyledTabPanel = styled(TabPanel)({
  padding: '4px 8px',
})

interface Props {
  selectedElement: string
  geometryEditable?: boolean
  activeElement?: string
  onClose: () => void
}

const ColumnForm = ({
  geometryEditable = true,
  selectedElement,
  activeElement,
  onClose,
}: Props): ReactElement | null => {
  const elementType = useElementType(selectedElement)

  const { columns, purlins } = useModelStore(state => state.model)
  const removeColumn = useModelStore(state => state.removeColumn)
  const updateColumn = useModelStore(state => state.updateColumn)
  const setTypeVisibility = useModelStore(state => state.setTypeVisibility)

  const { enqueueSnackbar } = useSnackbar()
  const { projectId }: { projectId?: string } = useParams()
  const client = useQueryClient()

  const setTensileTransmissionGraph = useTransmissionStore(
    state => state.setTensileTransmissionGraph,
  )

  const {
    modes: { isDrawMode },
    params: { verticalTransmitter, activeTab },
    actions: { setVerticalTransmitter, setActiveTab },
  } = useStructuralPlanningSelection()

  const invalidateResults = useResultsInvalidation()

  const getLabel = useElementLabel()

  const { mutateAsync, isLoading } = useMutation(
    (column: PlanLineElement) => createColumn.request(projectId, column),
    {
      onSuccess: async (data: PlanLineElement) => {
        setColumnStale(data)
        await Promise.all([
          client.invalidateQueries(getModel.getKey(projectId)),
          client.invalidateQueries(getVerticalTransmissionGraph.getKey(projectId)),
          client.invalidateQueries(getModel.getKey(projectId)),
          client.invalidateQueries(getModel.getKey(projectId)),
        ])

        invalidateResults(projectId as string)
        enqueueSnackbar('Stütze erfolgreich gespeichert', { variant: 'success' })
      },
      onError: (error: AxiosError) => {
        enqueueSnackbar(buildErrorMessage(error, 'Fehler beim Speichern des Stütze'), {
          variant: 'error',
        })
      },
    },
  )

  const { mutateAsync: handleUpdate, isLoading: isUpdateing } = useMutation(
    (column: PlanLineElement) => updateColumnReq.request(projectId, column),
    {
      onSuccess: async (data: PlanLineElement) => {
        setColumnStale(data)
        await Promise.all([
          client.invalidateQueries(getModel.getKey(projectId)),
          client.invalidateQueries(getVerticalTransmissionGraph.getKey(projectId)),
          client.invalidateQueries(getModel.getKey(projectId)),
        ])
        invalidateResults(projectId as string)
        enqueueSnackbar('Stütze erfolgreich gespeichert', { variant: 'success' })
      },
      onError: (error: AxiosError) => {
        enqueueSnackbar(buildErrorMessage(error, 'Fehler beim Speichern der Stütze'), {
          variant: 'error',
        })
      },
    },
  )

  const { mutateAsync: handleDelete, isLoading: isDeleting } = useMutation(
    (columnGuid: string) => {
      return deleteColumn.request(projectId, columnGuid)
    },
    {
      onSuccess: async () => {
        await Promise.all([
          client.invalidateQueries(getModel.getKey(projectId)),
          client.invalidateQueries(getVerticalTransmissionGraph.getKey(projectId)),
          client.invalidateQueries(getModel.getKey(projectId)),
          client.invalidateQueries(getProjectFiles.getKey(projectId, documentationRootGuid)),
        ])
        invalidateResults(projectId as string)
        enqueueSnackbar('Stütze erfolgreich gelöscht', { variant: 'success' })
      },
      onError: (error: Error | AxiosError) => {
        enqueueSnackbar(buildErrorMessage(error, 'Fehler beim Löschen der Stütze'), {
          variant: 'error',
        })
      },
    },
  )

  useBlockScene(isLoading || isDeleting || isUpdateing)

  const column = useMemo(() => {
    return find(columns, ['guid', selectedElement]) as PlanLineElement
  }, [columns, selectedElement])

  const purlin = useMemo(() => find(purlins, ['guid', activeElement]), [activeElement, purlins])

  const { setStale: setColumnStale } = useElementHistory({
    element: column,
    getCurrentElement: () => {
      const columns = useModelStore.getState().model.columns
      return find(columns, ['guid', selectedElement]) as PlanLineElement
    },
    removeElement: column => removeColumn(column.guid),
    resetElement: column => updateColumn(column),
    dependencies: [selectedElement],
  })

  useEffect(() => {
    setTypeVisibility('columns' as ElementTypes, true)
  }, [setTypeVisibility])

  useStructuralPlanningFormEsc(() => {
    // column has to be saved otherwise remove
    if (column?.is_local) {
      removeColumn(column.guid)
    }
    onClose()
  }, !!selectedElement)

  const { mutate: saveGraph, isLoading: isSavingGraph } = useSaveTensileTransmissionGraph(
    projectId as string,
  )

  const { mutate: recalculateTargets, isLoading: isRecalculating } = useMutation(
    () => recalculateTensileTargets.request(projectId, selectedElement),
    {
      onSuccess: async data => {
        setTensileTransmissionGraph(data)
        invalidateResults(projectId as string)
        enqueueSnackbar('Ziele erfolgreich neu berechnet', { variant: 'success' })
      },
      onError: (error: AxiosError) => {
        enqueueSnackbar(buildErrorMessage(error, 'Fehler beim der Berechnung der Ziele'), {
          variant: 'error',
        })
      },
    },
  )

  const selectedTab = useMemo(() => {
    if (activeTab && find(Object.entries(columnTabs), ([, e]) => e.tab === activeTab))
      return activeTab

    return columnTabs.geometry.tab
  }, [activeTab])

  if (!column) return null

  if (column.is_local) {
    return (
      <Stack direction="column" spacing={1} p={1}>
        <GeometryForm
          column={column}
          purlin={purlin}
          handleSubmit={column => mutateAsync(column)}
          handleDelete={() => removeColumn(column.guid)}
          showCreateLabel
        />
      </Stack>
    )
  }

  return (
    <Box>
      <TabContext value={selectedTab}>
        <Box
          sx={{
            borderBottom: 1,
            borderColor: 'divider',
            '& .MuiTab-root': {
              fontSize: 12,
              paddingX: ({ spacing }) => spacing(1.5),
            },
          }}
        >
          <TabList onChange={(_, value) => setActiveTab(value)} scrollButtons="auto">
            <Tab
              value={columnTabs.geometry.tab}
              label={columnTabs.geometry.label}
              data-cy="tab-geometry"
            />
            <Tab
              value={columnTabs.verticalTransmission.tab}
              label={columnTabs.verticalTransmission.label}
              data-cy="tab-vertical-transmission"
            />
            <Tab
              value={columnTabs.connector.tab}
              label={columnTabs.connector.label}
              data-cy="tab-connector"
            />
          </TabList>
        </Box>
        <StyledTabPanel value={columnTabs.connector.tab}>
          <TensileTransmitterList
            selectedElement={selectedElement}
            onSave={saveGraph}
            onRecalculate={() => recalculateTargets()}
            isRecalculating={isRecalculating}
            isLoading={isSavingGraph}
          />
        </StyledTabPanel>
        <StyledTabPanel value={columnTabs.geometry.tab}>
          <Stack direction="column" spacing={1}>
            <StructuralPlanningElementFormHeader
              elementGuid={selectedElement}
              label={getLabel(selectedElement)}
              onClose={onClose}
            />

            <CollapsibleAssemblyBoxSection label="Querschnitt/Aufbau" defaultExpanded={true}>
              <SingleElementCSForm
                selectedElement={selectedElement}
                elementType={elementType as ElementTypes}
              />
            </CollapsibleAssemblyBoxSection>

            <CollapsibleGeometryBoxSection defaultExpanded={true}>
              {geometryEditable ? (
                <GeometryForm
                  column={column}
                  purlin={purlin}
                  handleSubmit={() => handleUpdate(column)}
                  handleDelete={() => handleDelete(column.guid)}
                  isDeleting={isDeleting}
                />
              ) : (
                <Box display="flex" width="100%" justifyContent="flex-end">
                  <HotkeyDeleteButton
                    onClick={() => handleDelete(column.guid)}
                    loading={isDeleting}
                    data-cy="delete-column-btn"
                  >
                    Löschen
                  </HotkeyDeleteButton>
                </Box>
              )}
            </CollapsibleGeometryBoxSection>

            <CollapsibleSettingsBoxSection defaultExpanded={false}>
              <PlanSettingsForm element={column} setStale={setColumnStale} />
            </CollapsibleSettingsBoxSection>

            {column.storey && (
              <CollapsibleCopyBoxSection
                label="Kopieren"
                defaultExpanded={false}
                dataCy="column-form-copy-to-storey"
              >
                <CopyToOtherStoreys selectedElements={[selectedElement]} storey={column.storey} />
              </CollapsibleCopyBoxSection>
            )}
            <CollapsibleDocumentationBoxSection defaultExpanded={false}>
              <DocumentationItem itemGuid={selectedElement} />
            </CollapsibleDocumentationBoxSection>
            <CollapsibleProblemsBoxSection elementGuid={selectedElement} defaultExpanded={true} />
          </Stack>
        </StyledTabPanel>
        <StyledTabPanel value={columnTabs.verticalTransmission.tab}>
          <VerticalTransmitterList
            selectedElement={selectedElement}
            verticalTransmitter={verticalTransmitter}
            setVerticalTransmitter={(guid, elementGuid) =>
              setVerticalTransmitter(guid, elementGuid, isDrawMode)
            }
          />
        </StyledTabPanel>
      </TabContext>
    </Box>
  )
}

export default ColumnForm
